import React, { useEffect, useState, useRef } from 'react';
import { copyIcon } from '../../static/svgs';
import './MessageItem.css';
import DocumentEditCard from './DocumentEditCard';
import chatApi from '../../api/chatApi';


function normalizeMessageText(text) {
  const LABELS = {
    name: 'Наименование',
    description: 'Описание',
  };

  const formatObject = (obj) =>
    Object.entries(obj)
      .map(([key, value]) => {
        const label = LABELS[key] || key;
        return `<b>${label}:</b> ${value}`;
      })
      .join('\n');

  if (typeof text !== 'string') {
    if (text && typeof text === 'object' && !Array.isArray(text)) {
      return formatObject(text);
    }
    return String(text ?? '');
  }

  const trimmed = text.trim();

  if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        return formatObject(parsed);
      }
    } catch {
    }
  }

  return text;
}



function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getFileNameFromUrl(fileUrl) {
  const clean = String(fileUrl || '').split('?')[0].split('#')[0];
  const parts = clean.split('/');
  return safeDecode(parts[parts.length - 1] || 'document');
}

function getContentDispositionFileName(headers = {}) {
  const disposition = headers['content-disposition'] || headers['Content-Disposition'] || '';
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) {
    return safeDecode(utfMatch[1]);
  }

  const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] ? safeDecode(plainMatch[1]) : '';
}

function triggerBrowserDownload(blob, preferredName) {
  const downloadUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = downloadUrl;
  anchor.download = preferredName || 'document';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(downloadUrl);
}

function getGeneratedFileMeta(generatedFile) {
  if (!generatedFile) {
    return null;
  }

  if (typeof generatedFile === 'string') {
    const fileUrl = generatedFile.trim();
    if (!fileUrl) {
      return null;
    }

    return {
      fileUrl,
      displayName: getFileNameFromUrl(fileUrl),
      downloadMode: 'proxy',
    };
  }

  if (typeof generatedFile !== 'object' || Array.isArray(generatedFile)) {
    return null;
  }

  const fileUrl = String(generatedFile.fileUrl || '').trim();
  if (fileUrl) {
    return {
      ...generatedFile,
      fileUrl,
      fileName: generatedFile.fileName || getFileNameFromUrl(fileUrl),
      displayName: generatedFile.displayName || generatedFile.fileName || getFileNameFromUrl(fileUrl),
      downloadMode: 'proxy',
    };
  }

  const fileName = String(generatedFile.fileName || '').trim();
  if (fileName) {
    return {
      ...generatedFile,
      fileName,
      displayName: generatedFile.displayName || fileName,
      downloadMode: 'legacy',
    };
  }

  return null;
}

function getInlineEditedFiles(msg) {
  const existingEditedFiles = Array.isArray(msg?.editedFiles) ? msg.editedFiles : [];
  if (existingEditedFiles.length > 0) {
    return existingEditedFiles;
  }

  if (!msg?.originalFile || !msg?.editedFile) {
    return [];
  }

  return [{
    sessionId: msg.sessionId || '',
    version: Number(msg.version || 1),
    originalFileUrl: msg.originalFile,
    editedFileUrl: msg.editedFile,
    reviewFileUrl: msg.reviewFile || '',
    protocolFileUrl: msg.protocolFile || '',
  }];
}

function formatTraceDuration(durationMs) {
  const value = Number(durationMs);
  if (!Number.isFinite(value) || value < 0) return '';
  if (value < 1000) return `${Math.round(value)} мс`;
  return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)} с`;
}

function getSafeSourceUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return ['http:', 'https:'].includes(url.protocol) ? url.toString() : '';
  } catch {
    return '';
  }
}

function formatTraceElapsed(elapsedMs) {
  const totalSeconds = Math.max(0, Math.floor(Number(elapsedMs || 0) / 1000));
  if (totalSeconds < 60) return `${totalSeconds} сек`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes} мин ${seconds} сек` : `${minutes} мин`;
}

function getActiveTraceLabel(event = {}) {
  if (event.stage === 'web_search') return 'Ищет информацию в интернете';
  if (event.stage === 'knowledge_search') return 'Ищет информацию в базе знаний';
  if (event.stage === 'document_generation') return 'Создаёт документ';
  if (event.stage === 'generation' && event.status === 'streaming') return 'Пишет ответ';
  if (event.stage === 'generation') return 'Думает над ответом';
  return event.title || event.message || 'Анализирует запрос';
}

const ExecutionTrace = ({
  events = [],
  streaming = false,
  streamStartedAt = 0,
  streamElapsedMs = 0,
}) => {
  const [isOpen, setIsOpen] = useState(Boolean(streaming));
  const [elapsedMs, setElapsedMs] = useState(() => Number(streamElapsedMs || 0));
  const normalizedEvents = Array.isArray(events) ? events.filter(Boolean) : [];

  useEffect(() => {
    if (streaming && normalizedEvents.length > 0) {
      setIsOpen(true);
    }
  }, [streaming, normalizedEvents.length]);

  useEffect(() => {
    if (!streaming) {
      if (Number(streamElapsedMs) > 0) setElapsedMs(Number(streamElapsedMs));
      return undefined;
    }

    const localStartedAt = Number(streamStartedAt) > 0 ? Number(streamStartedAt) : Date.now();
    const updateElapsed = () => setElapsedMs(Math.max(0, Date.now() - localStartedAt));
    updateElapsed();
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [streaming, streamStartedAt, streamElapsedMs]);

  if (normalizedEvents.length === 0) return null;

  const activeEvent = [...normalizedEvents].reverse().find((event) => (
    event.status === 'running' || event.status === 'streaming'
  ));
  const sourceKeys = new Set();
  normalizedEvents.forEach((event) => {
    (Array.isArray(event.sources) ? event.sources : []).forEach((source) => {
      sourceKeys.add(source.url || source.id || `${source.title}-${source.page}`);
    });
  });
  const activeLabel = activeEvent ? getActiveTraceLabel(activeEvent) : '';
  const elapsedLabel = formatTraceElapsed(elapsedMs);

  return (
    <details
      className={`execution-trace ${streaming ? 'is-streaming' : ''}`}
      open={isOpen}
      onToggle={(event) => setIsOpen(event.currentTarget.open)}
    >
      <summary className="execution-trace-summary">
        {activeEvent ? (
          <>
            <span>{activeLabel}</span>
            <span className="execution-trace-ellipsis" aria-hidden="true" />
            <span className="execution-trace-live-time">{elapsedLabel}</span>
          </>
        ) : (
          <span>
            Обработка заняла {elapsedLabel}
            {sourceKeys.size > 0 ? ` · источников: ${sourceKeys.size}` : ''}
          </span>
        )}
      </summary>

      <div className="execution-trace-body">
        {normalizedEvents.map((event) => {
          const sources = Array.isArray(event.sources) ? event.sources : [];
          return (
            <div className="execution-trace-step" key={event.id}>
              <div className="execution-trace-step-header">
                <span className="execution-trace-title">{event.title}</span>
                {event.durationMs !== undefined && (
                  <span className="execution-trace-duration">{formatTraceDuration(event.durationMs)}</span>
                )}
              </div>
              {event.message && <div className="execution-trace-message">{event.message}</div>}

              {sources.length > 0 && (
                <div className="execution-trace-sources">
                  {sources.map((source, sourceIndex) => {
                    const safeUrl = getSafeSourceUrl(source.url);
                    return (
                      <div className="execution-trace-source" key={source.id || safeUrl || sourceIndex}>
                        <div className="execution-trace-source-title">
                          {safeUrl ? (
                            <a href={safeUrl} target="_blank" rel="noreferrer">{source.title || safeUrl}</a>
                          ) : (
                            <span>{source.title || `Источник ${sourceIndex + 1}`}</span>
                          )}
                          {source.page && <span className="execution-trace-page">{source.page}</span>}
                        </div>
                        {source.collectionName && (
                          <div className="execution-trace-meta">База: {source.collectionName}</div>
                        )}
                        {source.snippet && <div className="execution-trace-snippet">{source.snippet}</div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {event.contentPreview && (
                <details className="execution-trace-preview">
                  <summary>Что передано модели</summary>
                  <div>{event.contentPreview}</div>
                </details>
              )}
            </div>
          );
        })}
      </div>
    </details>
  );
};

const MessageItem = ({ msg, formatText, userId }) => {
  const [copied, setCopied] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const textRef = useRef(null);

  const handleCopy = async () => {
    try {
      if (!textRef.current) return;

      const html = textRef.current.innerHTML;
      const text = textRef.current.innerText;

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Ошибка копирования:', error);
    }
  };

  const normalizedText = normalizeMessageText(msg.text);
  const generatedFileMeta = getGeneratedFileMeta(msg.generatedFile);
  const inlineEditedFiles = getInlineEditedFiles(msg);
  const executionTrace = Array.isArray(msg.executionTrace) ? msg.executionTrace : [];
  const documentEditMessage = inlineEditedFiles.length > 0
    ? { ...msg, editedFiles: inlineEditedFiles }
    : msg;

  // Формируем URL для скачивания по ТЗ
  const getDownloadUrl = () => {
    if (!generatedFileMeta) return '';
    const { fileName, displayName } = generatedFileMeta;
    const baseUrl = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';
    return `${baseUrl}/api/files/${fileName}?displayName=${encodeURIComponent(displayName)}`;
  };

  // Скачивание файла через window.open (обходит React Router)
  const handleDownload = async (e) => {
    e.preventDefault();
    setDownloadError('');

    if (!generatedFileMeta) return;

    if (generatedFileMeta.downloadMode === 'legacy') {
      const url = getDownloadUrl();
      if (!url) return;
      window.open(url, '_blank');
      return;
    }

    if (!userId || !msg.id || !generatedFileMeta.fileUrl) {
      setDownloadError('Не удалось подготовить ссылку для скачивания');
      return;
    }

    try {
      const response = await chatApi.downloadEditedFile(userId, msg.id, generatedFileMeta.fileUrl);
      const nameFromHeader = getContentDispositionFileName(response.headers);
      triggerBrowserDownload(
        response.data,
        nameFromHeader || generatedFileMeta.displayName || getFileNameFromUrl(generatedFileMeta.fileUrl)
      );
    } catch (error) {
      console.error('Generated file download error:', error);
      setDownloadError('Не удалось скачать файл');
    }
  };

  return (
    <div
      className={`message ${msg.sender === 'BotTax' ? 'received' : 'sent'
        }`}
    >
      {!msg.file_path && (
        <>
          {msg.sender === 'BotTax' && (
            <ExecutionTrace
              events={executionTrace}
              streaming={Boolean(msg.streaming)}
              streamStartedAt={msg.streamStartedAt}
              streamElapsedMs={msg.streamElapsedMs}
            />
          )}

          {normalizedText && (
            <div
              ref={textRef}
              className="message-text"
              dangerouslySetInnerHTML={{
                __html: formatText(
                  normalizedText,
                  msg.sender === 'BotTax' ? msg.urls : '',
                  msg.sender === 'BotTax' ? msg.urls_title : '',
                ),
              }}
            />
          )}

          {generatedFileMeta && (
            <div className="generated-file-download">
              <button
                className="download-button"
                onClick={handleDownload}
              >
                <span>Скачать {generatedFileMeta.displayName}</span>
              </button>
              {downloadError && <div className="download-error">{downloadError}</div>}
            </div>
          )}

          {inlineEditedFiles.length > 0 && (
            <DocumentEditCard message={documentEditMessage} userId={userId} />
          )}

          {normalizedText && <label className="copy_icon_message" onClick={handleCopy}>
            {copied ? (
              <span className="copied-text">Скопировано</span>
            ) : (
              copyIcon
            )}
          </label>}
        </>
      )}
    </div>
  );
};

export default MessageItem;
